import type { RouteRecordRaw } from 'vue-router'
import { permissions } from '@/config/permissions'
import { memberLedgerKinds } from '@/config/member-ledgers'
import { memberBindingKinds } from '@/config/member-bindings'

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: '登录', public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },
      {
        path: 'content/yidiancan-brands',
        name: 'yidiancan-brand-list',
        component: () => import('@/views/content/YidiancanBrandListView.vue'),
        meta: {
          title: '易点餐品牌管理',
          permission: permissions.advertisingView,
          menuKey: '/content/yidiancan-brands',
        },
      },
      {
        path: 'content/saturday',
        name: 'saturday-config',
        component: () => import('@/views/content/SaturdayConfigView.vue'),
        meta: { title: '五色星期六', permission: permissions.advertisingView },
      },
      ...(['category', 'goods'] as const).flatMap((kind) => {
        const path = kind === 'category' ? 'catalog/zhongying' : 'products/zhongying'
        const label = kind === 'category' ? '中赢商品分类' : '中赢商品'
        return [
          {
            path,
            name: `zhongying-${kind}-list`,
            component: () => import('@/views/zhongying/ZhongyingListView.vue'),
            props: { kind },
            meta: {
              title: label,
              permission: permissions.goodsView,
              menuKey: `/${path}`,
            },
          },
          {
            path: `${path}/:id/edit`,
            name: `zhongying-${kind}-edit`,
            component: () => import('@/views/zhongying/ZhongyingFormView.vue'),
            props: { kind },
            meta: {
              title: `编辑${label}`,
              permission: permissions.goodsView,
              menuKey: `/${path}`,
              hidden: true,
            },
          },
          {
            path: `${path}/:id`,
            name: `zhongying-${kind}-detail`,
            component: () => import('@/views/zhongying/ZhongyingFormView.vue'),
            props: { kind },
            meta: {
              title: `${label}详情`,
              permission: permissions.goodsView,
              menuKey: `/${path}`,
              hidden: true,
            },
          },
        ]
      }),
      ...(['category', 'goods'] as const).flatMap((kind) => {
        const path = kind === 'category' ? 'catalog/yunzhonghe' : 'products/yunzhonghe'
        const label = kind === 'category' ? '云中鹤商品分类' : '云中鹤商品'
        return [
          {
            path,
            name: `yunzhonghe-${kind}-list`,
            component: () => import('@/views/yunzhonghe/YunzhongheListView.vue'),
            props: { kind },
            meta: {
              title: label,
              permission: permissions.goodsView,
              menuKey: `/${path}`,
            },
          },
          {
            path: `${path}/:id/edit`,
            name: `yunzhonghe-${kind}-edit`,
            component: () => import('@/views/yunzhonghe/YunzhongheFormView.vue'),
            props: { kind },
            meta: {
              title: `编辑${label}`,
              permission: permissions.goodsView,
              menuKey: `/${path}`,
              hidden: true,
            },
          },
          {
            path: `${path}/:id`,
            name: `yunzhonghe-${kind}-detail`,
            component: () => import('@/views/yunzhonghe/YunzhongheFormView.vue'),
            props: { kind },
            meta: {
              title: `${label}详情`,
              permission: permissions.goodsView,
              menuKey: `/${path}`,
              hidden: true,
            },
          },
        ]
      }),
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: {
          title: '首页',
          permission: permissions.dashboardView,
          menuKey: '/dashboard',
        },
      },
      {
        path: 'organizations/companies',
        name: 'company-list',
        component: () => import('@/views/company/CompanyListView.vue'),
        meta: {
          title: '公司管理',
          permission: permissions.companyView,
          menuKey: '/organizations/companies',
        },
      },
      {
        path: 'organizations/companies/new',
        name: 'company-create',
        component: () => import('@/views/company/CompanyFormView.vue'),
        meta: { title: '新建公司', permission: permissions.companyEdit, hidden: true },
      },
      {
        path: 'organizations/companies/:id/edit',
        name: 'company-edit',
        component: () => import('@/views/company/CompanyFormView.vue'),
        meta: { title: '编辑公司', permission: permissions.companyEdit, hidden: true },
      },
      {
        path: 'organizations/companies/:id',
        name: 'company-detail',
        component: () => import('@/views/company/CompanyFormView.vue'),
        meta: { title: '公司详情', permission: permissions.companyView, hidden: true },
      },
      {
        path: 'organizations/merchants',
        name: 'merchant-list',
        component: () => import('@/views/merchant/MerchantListView.vue'),
        meta: {
          title: '商家管理',
          permission: permissions.merchantView,
          menuKey: '/organizations/merchants',
        },
      },
      {
        path: 'organizations/merchants/new',
        name: 'merchant-create',
        component: () => import('@/views/merchant/MerchantFormView.vue'),
        meta: { title: '新建商家', permission: permissions.merchantEdit, hidden: true },
      },
      {
        path: 'organizations/merchant-capital',
        name: 'merchant-capital',
        component: () => import('@/views/merchant/CapitalListView.vue'),
        meta: {
          title: '资金记录',
          permission: permissions.merchantView,
          menuKey: '/organizations/merchant-capital',
        },
      },
      {
        path: 'organizations/merchants/:id/edit',
        name: 'merchant-edit',
        component: () => import('@/views/merchant/MerchantFormView.vue'),
        meta: { title: '编辑商家', permission: permissions.merchantEdit, hidden: true },
      },
      {
        path: 'organizations/merchants/:id',
        name: 'merchant-detail',
        component: () => import('@/views/merchant/MerchantFormView.vue'),
        meta: { title: '商家详情', permission: permissions.merchantView, hidden: true },
      },
      {
        path: 'catalog',
        name: 'catalog-list',
        component: () => import('@/views/catalog/CatalogListView.vue'),
        meta: {
          title: '前台类目',
          permission: permissions.goodsView,
          menuKey: '/catalog',
        },
      },
      {
        path: 'catalog/new/:position',
        name: 'catalog-create',
        component: () => import('@/views/catalog/CatalogFormView.vue'),
        meta: { title: '新增类目', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'catalog/:position/:id/edit',
        name: 'catalog-edit',
        component: () => import('@/views/catalog/CatalogFormView.vue'),
        meta: { title: '编辑类目', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'catalog/business/:kind',
        name: 'business-category-list',
        component: () => import('@/views/catalog/BusinessCategoryListView.vue'),
        meta: { title: '业务分类', permission: permissions.goodsView },
      },
      {
        path: 'catalog/business/:kind/new',
        name: 'business-category-create',
        component: () => import('@/views/catalog/BusinessCategoryFormView.vue'),
        meta: { title: '新建分类', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'catalog/business/:kind/:id/edit',
        name: 'business-category-edit',
        component: () => import('@/views/catalog/BusinessCategoryFormView.vue'),
        meta: { title: '编辑分类', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'catalog/business/:kind/:id',
        name: 'business-category-detail',
        component: () => import('@/views/catalog/BusinessCategoryFormView.vue'),
        meta: { title: '分类详情', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'catalog/poster-categories',
        name: 'poster-category-list',
        component: () => import('@/views/catalog/PosterCategoryListView.vue'),
        meta: {
          title: '海报分类',
          permission: permissions.advertisingView,
          menuKey: '/catalog/poster-categories',
        },
      },
      {
        path: 'catalog/tags',
        name: 'tag-category-list',
        component: () => import('@/views/catalog/TagCategoryListView.vue'),
        meta: {
          title: '标签分类',
          permission: permissions.tagCategoryView,
          menuKey: '/catalog/tags',
        },
      },
      {
        path: 'catalog/tags/new',
        name: 'tag-category-create',
        component: () => import('@/views/catalog/TagCategoryFormView.vue'),
        meta: { title: '新建标签', permission: permissions.tagCategoryView, hidden: true },
      },
      {
        path: 'catalog/tags/:id/edit',
        name: 'tag-category-edit',
        component: () => import('@/views/catalog/TagCategoryFormView.vue'),
        meta: { title: '编辑标签', permission: permissions.tagCategoryView, hidden: true },
      },
      {
        path: 'catalog/tags/:id',
        name: 'tag-category-detail',
        component: () => import('@/views/catalog/TagCategoryFormView.vue'),
        meta: { title: '标签详情', permission: permissions.tagCategoryView, hidden: true },
      },
      {
        path: 'products/gifts',
        name: 'gift-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        meta: {
          title: '平替区商品',
          permission: permissions.goodsView,
          menuKey: '/products/gifts',
        },
      },
      {
        path: 'products/gifts/:id/edit',
        name: 'gift-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        meta: { title: '编辑平替区商品', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/gifts/:id',
        name: 'gift-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        meta: { title: '平替区商品详情', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/explosive',
        name: 'explosive-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        props: { channel: 'explosive' },
        meta: {
          title: '限量购商品',
          permission: permissions.goodsView,
          menuKey: '/products/explosive',
        },
      },
      {
        path: 'products/explosive/:id/edit',
        name: 'explosive-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'explosive' },
        meta: { title: '编辑限量购商品', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/explosive/:id',
        name: 'explosive-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'explosive' },
        meta: { title: '限量购商品详情', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/superior',
        name: 'superior-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        props: { channel: 'superior' },
        meta: {
          title: '中赢商品',
          permission: permissions.goodsView,
          menuKey: '/products/superior',
        },
      },
      {
        path: 'products/superior/:id/edit',
        name: 'superior-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'superior' },
        meta: { title: '编辑中赢商品', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/superior/:id',
        name: 'superior-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'superior' },
        meta: { title: '中赢商品详情', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/district',
        name: 'district-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        props: { channel: 'district' },
        meta: {
          title: '区代商品',
          permission: permissions.goodsView,
          menuKey: '/products/district',
        },
      },
      {
        path: 'products/district/:id/edit',
        name: 'district-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'district' },
        meta: { title: '编辑区代商品', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/district/:id',
        name: 'district-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'district' },
        meta: { title: '区代商品详情', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/courses',
        name: 'course-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        props: { channel: 'course' },
        meta: {
          title: '课程商品',
          permission: permissions.goodsView,
          menuKey: '/products/courses',
        },
      },
      {
        path: 'products/courses/:id/edit',
        name: 'course-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'course' },
        meta: { title: '编辑课程商品', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/courses/:id',
        name: 'course-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'course' },
        meta: { title: '课程商品详情', permission: permissions.goodsView, hidden: true },
      },
      {
        path: 'products/presents',
        name: 'present-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        props: { channel: 'present' },
        meta: {
          title: '零元购商品',
          permission: permissions.goodsView,
          menuKey: '/products/presents',
        },
      },
      {
        path: 'products/presents/:id/edit',
        name: 'present-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'present' },
        meta: {
          title: '编辑零元购商品',
          permission: permissions.goodsView,
          hidden: true,
          menuKey: '/products/presents',
        },
      },
      {
        path: 'products/presents/:id',
        name: 'present-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'present' },
        meta: {
          title: '零元购商品详情',
          permission: permissions.goodsView,
          hidden: true,
          menuKey: '/products/presents',
        },
      },
      {
        path: 'products/star',
        name: 'star-product-list',
        component: () => import('@/views/product/GiftProductListView.vue'),
        props: { channel: 'star' },
        meta: {
          title: '9.9元商品',
          permission: permissions.goodsView,
          menuKey: '/products/star',
        },
      },
      {
        path: 'products/star/:id/edit',
        name: 'star-product-edit',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'star' },
        meta: {
          title: '编辑9.9元商品',
          permission: permissions.goodsView,
          hidden: true,
          menuKey: '/products/star',
        },
      },
      {
        path: 'products/star/:id',
        name: 'star-product-detail',
        component: () => import('@/views/product/GiftProductFormView.vue'),
        props: { channel: 'star' },
        meta: {
          title: '9.9元商品详情',
          permission: permissions.goodsView,
          hidden: true,
          menuKey: '/products/star',
        },
      },
      ...(['app', 'mini'] as const).flatMap((kind) => {
        const path = kind === 'app' ? 'recommendations' : 'mini-recommendations'
        const label = kind === 'app' ? '抢爆品' : '小程序商品推荐'
        return [
          {
            path: `products/${path}`,
            name: `${kind}-recommendation-list`,
            component: () => import('@/views/product/RecommendationListView.vue'),
            props: { kind },
            meta: { title: label, permission: permissions.goodsView, menuKey: `/products/${path}` },
          },
          {
            path: `products/${path}/new`,
            name: `${kind}-recommendation-create`,
            component: () => import('@/views/product/RecommendationFormView.vue'),
            props: { kind },
            meta: { title: `新增${label}`, permission: permissions.goodsView, hidden: true },
          },
          {
            path: `products/${path}/:id/edit`,
            name: `${kind}-recommendation-edit`,
            component: () => import('@/views/product/RecommendationFormView.vue'),
            props: { kind },
            meta: { title: `编辑${label}`, permission: permissions.goodsView, hidden: true },
          },
        ]
      }),
      ...(['free', 'exclusive', 'topic'] as const).flatMap((kind) => {
        const path = kind === 'topic' ? 'topics' : kind
        const label = kind === 'free' ? '免单商品' : kind === 'exclusive' ? '专享商品' : '专题商品'
        const component = () => import('@/views/product/CampaignProductFormView.vue')
        return [
          {
            path: `products/${path}`,
            name: `${kind}-product-list`,
            component: () => import('@/views/product/CampaignProductListView.vue'),
            props: { kind },
            meta: { title: label, permission: permissions.goodsView, menuKey: `/products/${path}` },
          },
          {
            path: `products/${path}/new`,
            name: `${kind}-product-create`,
            component,
            props: { kind },
            meta: { title: `新增${label}`, permission: permissions.goodsView, hidden: true },
          },
          {
            path: `products/${path}/:id/edit`,
            name: `${kind}-product-edit`,
            component,
            props: { kind },
            meta: { title: `编辑${label}`, permission: permissions.goodsView, hidden: true },
          },
          {
            path: `products/${path}/:id`,
            name: `${kind}-product-detail`,
            component,
            props: { kind },
            meta: { title: `${label}详情`, permission: permissions.goodsView, hidden: true },
          },
        ]
      }),
      ...(
        [
          'gift',
          'explosive',
          'superior',
          'yunzhonghe',
          'district',
          'course',
          'star',
          'present',
          'sendMore',
        ] as const
      ).flatMap((kind) => {
        const path =
          kind === 'gift'
            ? 'gifts'
            : kind === 'course'
              ? 'courses'
              : kind === 'present'
                ? 'presents'
                : kind === 'sendMore'
                  ? 'send-more'
                  : kind
        return [
          {
            path: `orders/${path}`,
            name: `${kind}-order-list`,
            component: () => import('@/views/order/CoreOrderListView.vue'),
            props: { kind },
            meta: {
              title: '订单管理',
              permission: permissions.ordersView,
              menuKey: `/orders/${path}`,
            },
          },
          {
            path: `orders/${path}/:id`,
            name: `${kind}-order-detail`,
            component: () => import('@/views/order/CoreOrderDetailView.vue'),
            props: { kind },
            meta: {
              title: '订单详情',
              permission: permissions.ordersView,
              hidden: true,
              menuKey: `/orders/${path}`,
            },
          },
          ...(kind === 'star' || kind === 'yunzhonghe'
            ? []
            : [
                {
                  path: `orders/${path}/:id/commission`,
                  name: `${kind}-order-commission`,
                  component: () => import('@/views/order/OrderCommissionView.vue'),
                  props: { source: 'core', kind },
                  meta: {
                    title: '分佣明细',
                    permission: permissions.ordersView,
                    hidden: true,
                    menuKey: `/orders/${path}`,
                  },
                },
              ]),
        ]
      }),
      ...(
        [
          'taobao',
          'jd',
          'vip',
          'eleme',
          'qipiao',
          'didi',
          'live',
          'oil',
          'meituan',
          'finance',
        ] as const
      ).flatMap((kind) => {
        const list = {
          path: `orders/${kind}`,
          name: `${kind}-order-list`,
          component: () => import('@/views/order/ExternalOrderListView.vue'),
          props: { kind },
          meta: {
            title:
              kind === 'qipiao'
                ? '奇票电影票订单'
                : kind === 'didi'
                  ? '滴滴订单'
                  : kind === 'vip'
                    ? '唯品会订单'
                    : kind === 'eleme'
                      ? '饿了么订单'
                      : '订单管理',
            permission: permissions.ordersView,
            menuKey: `/orders/${kind}`,
          },
        }
        return kind === 'live'
          ? [list]
          : [
              list,
              {
                path: `orders/${kind}/:id`,
                name: `${kind}-order-detail`,
                component: () => import('@/views/order/ExternalOrderDetailView.vue'),
                props: { kind },
                meta: {
                  title: '订单详情',
                  permission: permissions.ordersView,
                  hidden: true,
                  menuKey: `/orders/${kind}`,
                },
              },
            ]
      }),
      {
        path: 'orders/unshipped',
        name: 'unshipped-order-list',
        component: () => import('@/views/order/UnshippedOrderListView.vue'),
        meta: { title: '未发货订单', permission: permissions.ordersView, hidden: true },
      },
      {
        path: 'orders/after-sales',
        name: 'after-sale-list',
        component: () => import('@/views/order/AfterSaleListView.vue'),
        meta: {
          title: '售后管理',
          permission: permissions.ordersView,
          menuKey: '/orders/after-sales',
        },
      },
      {
        path: 'orders/after-sales/:id',
        name: 'after-sale-detail',
        component: () => import('@/views/order/AfterSaleDetailView.vue'),
        meta: {
          title: '售后详情',
          permission: permissions.ordersView,
          hidden: true,
          menuKey: '/orders/after-sales',
        },
      },
      {
        path: 'members',
        name: 'member-list',
        component: () => import('@/views/member/MemberListView.vue'),
        meta: { title: '用户管理', permission: permissions.membersView, menuKey: '/members' },
      },
      {
        path: 'members/statistics',
        name: 'member-statistics',
        component: () => import('@/views/member/MemberStatisticsView.vue'),
        meta: {
          title: '用户统计',
          permission: permissions.membersView,
          menuKey: '/members/statistics',
        },
      },
      {
        path: 'members/district-agents',
        name: 'district-agent-list',
        component: () => import('@/views/member/DistrictAgentListView.vue'),
        meta: {
          title: '区县代理',
          permission: permissions.membersView,
          menuKey: '/members/district-agents',
        },
      },
      {
        path: 'members/district-agents/:id',
        name: 'district-agent-detail',
        component: () => import('@/views/member/DistrictAgentDetailView.vue'),
        meta: { title: '区县代理档案', permission: permissions.membersView, hidden: true },
      },
      {
        path: 'members/district-agents/:userId/commissions',
        name: 'district-agent-commissions',
        component: () => import('@/views/member/DistrictCommissionView.vue'),
        meta: { title: '区县代理分佣', permission: permissions.membersView, hidden: true },
      },
      {
        path: 'members/angel-partners',
        name: 'angel-partner-list',
        component: () => import('@/views/member/AngelPartnerListView.vue'),
        meta: {
          title: '天使合伙人',
          permission: permissions.membersView,
          menuKey: '/members/angel-partners',
        },
      },
      {
        path: 'members/angel-partners/:id',
        name: 'angel-partner-detail',
        component: () => import('@/views/member/AngelPartnerDetailView.vue'),
        meta: { title: '天使合伙人档案', permission: permissions.membersView, hidden: true },
      },
      ...memberBindingKinds.map((kind) => {
        const path = kind === 'wechat' ? 'wechat-unbind' : 'taobao-binding'
        return {
          path: `members/${path}`,
          name: `member-${kind}-binding`,
          component: () => import('@/views/member/MemberBindingView.vue'),
          props: { kind },
          meta: {
            title: kind === 'wechat' ? '解绑微信' : '淘宝授权处理',
            permission: permissions.membersView,
            menuKey: `/members/${path}`,
          },
        }
      }),
      {
        path: 'members/:id',
        name: 'member-detail',
        component: () => import('@/views/member/MemberDetailView.vue'),
        meta: { title: '用户详情', permission: permissions.membersView, hidden: true },
      },
      {
        path: 'members/:id/edit',
        name: 'member-edit',
        component: () => import('@/views/member/MemberEditView.vue'),
        meta: { title: '编辑用户', permission: permissions.membersEdit, hidden: true },
      },
      ...memberLedgerKinds.map((kind) => ({
        path: `members/:id/${kind}`,
        name: `member-${kind}`,
        component: () => import('@/views/member/MemberLedgerView.vue'),
        props: { kind },
        meta: { title: '用户账户明细', permission: permissions.membersView, hidden: true },
      })),
      {
        path: 'members/:id/upgrades',
        name: 'member-upgrade-history',
        component: () => import('@/views/member/MemberUpgradeHistoryView.vue'),
        meta: { title: '用户升级明细', permission: permissions.membersView, hidden: true },
      },
      {
        path: 'finance/recharges',
        name: 'balance-recharge-list',
        component: () => import('@/views/finance/BalanceRechargeListView.vue'),
        meta: {
          title: '余额充值审核',
          permission: permissions.financeAudit,
          menuKey: '/finance/recharges',
        },
      },
      {
        path: 'finance/recharges/:id',
        name: 'balance-recharge-detail',
        component: () => import('@/views/finance/BalanceRechargeDetailView.vue'),
        meta: { title: '余额充值详情', permission: permissions.financeAudit, hidden: true },
      },
      ...(['balance', 'currency', 'youmi'] as const).map((kind) => ({
        path: `finance/statements/${kind}`,
        name: `finance-${kind}-statement`,
        component: () => import('@/views/finance/FinanceStatementListView.vue'),
        props: { kind },
        meta: {
          title: kind === 'balance' ? '余额流水' : kind === 'currency' ? '电子币流水' : '优米流水',
          permission: permissions.financeView,
          menuKey: `/finance/statements/${kind}`,
        },
      })),
      {
        path: 'finance/surplus-balances',
        name: 'finance-surplus-balances',
        component: () => import('@/views/finance/SurplusBalanceListView.vue'),
        meta: {
          title: '每日剩余余额',
          permission: permissions.financeView,
          menuKey: '/finance/surplus-balances',
        },
      },
      {
        path: 'live',
        name: 'live-list',
        component: () => import('@/views/live/LiveListView.vue'),
        meta: { title: '直播管理', permission: permissions.liveView, menuKey: '/live' },
      },
      {
        path: 'live/new',
        name: 'live-create',
        component: () => import('@/views/live/LiveFormView.vue'),
        meta: { title: '新建直播', permission: permissions.liveView, hidden: true },
      },
      {
        path: 'live/applications',
        name: 'live-application-list',
        component: () => import('@/views/live/LiveApplicationListView.vue'),
        meta: {
          title: '直播申请',
          permission: permissions.liveView,
          menuKey: '/live/applications',
        },
      },
      {
        path: 'live/applications/:id',
        name: 'live-application-detail',
        component: () => import('@/views/live/LiveApplicationDetailView.vue'),
        meta: { title: '直播申请详情', permission: permissions.liveView, hidden: true },
      },
      {
        path: 'live/:id/edit',
        name: 'live-edit',
        component: () => import('@/views/live/LiveFormView.vue'),
        meta: { title: '编辑直播', permission: permissions.liveView, hidden: true },
      },
      {
        path: 'live/:id',
        name: 'live-detail',
        component: () => import('@/views/live/LiveFormView.vue'),
        meta: { title: '直播详情', permission: permissions.liveView, hidden: true },
      },
      {
        path: 'content/banners',
        name: 'banner-list',
        component: () => import('@/views/content/BannerListView.vue'),
        meta: {
          title: 'App Banner',
          permission: permissions.advertisingView,
          menuKey: '/content/banners',
        },
      },
      {
        path: 'content/banners/new',
        name: 'banner-create',
        component: () => import('@/views/content/BannerFormView.vue'),
        meta: { title: '新增 App Banner', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/banners/:id/edit',
        name: 'banner-edit',
        component: () => import('@/views/content/BannerFormView.vue'),
        meta: { title: '编辑 App Banner', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/banners/:id',
        name: 'banner-detail',
        component: () => import('@/views/content/BannerFormView.vue'),
        meta: { title: 'App Banner 详情', permission: permissions.advertisingView, hidden: true },
      },
      ...(['wechat', 'oil'] as const).flatMap((kind) => {
        const label = kind === 'wechat' ? '小程序 Banner' : '加油 Banner'
        const path = `content/${kind}-banners`
        return [
          {
            path,
            name: `${kind}-banner-list`,
            component: () => import('@/views/content/BannerListView.vue'),
            props: { kind },
            meta: { title: label, permission: permissions.contentView, menuKey: `/${path}` },
          },
          {
            path: `${path}/new`,
            name: `${kind}-banner-create`,
            component: () => import('@/views/content/BannerFormView.vue'),
            props: { kind },
            meta: { title: `新增${label}`, permission: permissions.contentView, hidden: true },
          },
          {
            path: `${path}/:id/edit`,
            name: `${kind}-banner-edit`,
            component: () => import('@/views/content/BannerFormView.vue'),
            props: { kind },
            meta: { title: `编辑${label}`, permission: permissions.contentView, hidden: true },
          },
          {
            path: `${path}/:id`,
            name: `${kind}-banner-detail`,
            component: () => import('@/views/content/BannerFormView.vue'),
            props: { kind },
            meta: { title: `${label}详情`, permission: permissions.contentView, hidden: true },
          },
        ]
      }),
      {
        path: 'content/ad-screens',
        name: 'ad-screen-list',
        component: () => import('@/views/content/AdScreenListView.vue'),
        meta: {
          title: '广告屏管理',
          permission: permissions.advertisingView,
          menuKey: '/content/ad-screens',
        },
      },
      {
        path: 'content/ad-screens/new',
        name: 'ad-screen-create',
        component: () => import('@/views/content/AdScreenFormView.vue'),
        meta: { title: '新增广告屏', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/ad-screens/:id/edit',
        name: 'ad-screen-edit',
        component: () => import('@/views/content/AdScreenFormView.vue'),
        meta: { title: '编辑广告屏', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/ad-screens/:id',
        name: 'ad-screen-detail',
        component: () => import('@/views/content/AdScreenFormView.vue'),
        meta: { title: '广告屏详情', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/popups',
        name: 'popup-list',
        component: () => import('@/views/content/PopupListView.vue'),
        meta: {
          title: '弹窗管理',
          permission: permissions.advertisingView,
          menuKey: '/content/popups',
        },
      },
      {
        path: 'content/popups/new',
        name: 'popup-create',
        component: () => import('@/views/content/PopupFormView.vue'),
        meta: { title: '新增弹窗', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/popups/:id/edit',
        name: 'popup-edit',
        component: () => import('@/views/content/PopupFormView.vue'),
        meta: { title: '编辑弹窗', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/popups/:id',
        name: 'popup-detail',
        component: () => import('@/views/content/PopupFormView.vue'),
        meta: { title: '弹窗详情', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/functions',
        name: 'functional-list',
        component: () => import('@/views/content/FunctionalListView.vue'),
        meta: {
          title: '功能管理',
          permission: permissions.contentView,
          menuKey: '/content/functions',
        },
      },
      {
        path: 'content/functions/new',
        name: 'functional-create',
        component: () => import('@/views/content/FunctionalFormView.vue'),
        meta: { title: '新增功能', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/functions/:id/edit',
        name: 'functional-edit',
        component: () => import('@/views/content/FunctionalFormView.vue'),
        meta: { title: '编辑功能', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/functions/:id',
        name: 'functional-detail',
        component: () => import('@/views/content/FunctionalFormView.vue'),
        meta: { title: '功能详情', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/special-zone',
        name: 'special-zone',
        component: () => import('@/views/content/SpecialZoneView.vue'),
        meta: {
          title: '特色专区',
          permission: permissions.contentView,
          menuKey: '/content/special-zone',
        },
      },
      {
        path: 'content/hot-activity',
        name: 'hot-activity',
        component: () => import('@/views/content/HotActivityView.vue'),
        meta: {
          title: '热门活动',
          permission: permissions.contentView,
          menuKey: '/content/hot-activity',
        },
      },
      {
        path: 'content/superior-search',
        name: 'superior-search',
        component: () => import('@/views/content/SuperiorSearchView.vue'),
        meta: {
          title: '自营搜索',
          permission: permissions.contentView,
          menuKey: '/content/superior-search',
        },
      },
      {
        path: 'content/circles',
        name: 'circle-list',
        component: () => import('@/views/content/CircleListView.vue'),
        meta: {
          title: '圈子管理',
          permission: permissions.contentView,
          menuKey: '/content/circles',
        },
      },
      {
        path: 'content/circles/:id/edit',
        name: 'circle-edit',
        component: () => import('@/views/content/CircleDetailView.vue'),
        meta: { title: '编辑圈子内容', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/circles/:id',
        name: 'circle-detail',
        component: () => import('@/views/content/CircleDetailView.vue'),
        meta: { title: '圈子详情', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/community',
        name: 'community-list',
        component: () => import('@/views/content/CommunityListView.vue'),
        meta: {
          title: '社区管理',
          permission: permissions.contentView,
          menuKey: '/content/community',
        },
      },
      {
        path: 'content/community/new',
        name: 'community-create',
        component: () => import('@/views/content/CommunityFormView.vue'),
        meta: { title: '新增社区内容', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/community/:id/edit',
        name: 'community-edit',
        component: () => import('@/views/content/CommunityFormView.vue'),
        meta: { title: '编辑社区内容', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/materials',
        name: 'material-list',
        component: () => import('@/views/content/MaterialListView.vue'),
        meta: {
          title: '宣传素材',
          permission: permissions.contentView,
          menuKey: '/content/materials',
        },
      },
      {
        path: 'content/materials/new',
        name: 'material-create',
        component: () => import('@/views/content/MaterialFormView.vue'),
        meta: { title: '新增宣传素材', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/materials/:id/edit',
        name: 'material-edit',
        component: () => import('@/views/content/MaterialFormView.vue'),
        meta: { title: '编辑宣传素材', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/materials/:id',
        name: 'material-detail',
        component: () => import('@/views/content/MaterialFormView.vue'),
        meta: { title: '宣传素材详情', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/free-activity',
        name: 'free-activity',
        component: () => import('@/views/content/FreeActivityView.vue'),
        meta: {
          title: '免单活动',
          permission: permissions.contentView,
          menuKey: '/content/free-activity',
        },
      },
      {
        path: 'content/star-podcasts',
        name: 'star-podcast-list',
        component: () => import('@/views/content/StarPodcastListView.vue'),
        meta: {
          title: '优米星播客',
          permission: permissions.contentView,
          menuKey: '/content/star-podcasts',
        },
      },
      {
        path: 'content/star-podcasts/new',
        name: 'star-podcast-create',
        component: () => import('@/views/content/StarPodcastFormView.vue'),
        meta: { title: '新增优米星播客', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/star-podcasts/:id/edit',
        name: 'star-podcast-edit',
        component: () => import('@/views/content/StarPodcastFormView.vue'),
        meta: { title: '编辑优米星播客', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/star-podcasts/:id/comments',
        name: 'star-podcast-comments',
        component: () => import('@/views/content/StarPodcastCommentsView.vue'),
        meta: { title: '星播客评论', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/star-podcasts/:id',
        name: 'star-podcast-detail',
        component: () => import('@/views/content/StarPodcastFormView.vue'),
        meta: { title: '优米星播客详情', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/system-upgrade',
        name: 'system-upgrade',
        component: () => import('@/views/content/SystemUpgradeView.vue'),
        meta: {
          title: '系统升级',
          permission: permissions.contentView,
          menuKey: '/content/system-upgrade',
        },
      },
      {
        path: 'content/oil-stations',
        name: 'oil-station-list',
        component: () => import('@/views/content/OilStationListView.vue'),
        meta: {
          title: '加油站管理',
          permission: permissions.contentView,
          menuKey: '/content/oil-stations',
        },
      },
      {
        path: 'content/invitation-posters',
        name: 'invitation-poster-list',
        component: () => import('@/views/content/InvitationPosterListView.vue'),
        meta: {
          title: '邀请海报',
          permission: permissions.advertisingView,
          menuKey: '/content/invitation-posters',
        },
      },
      {
        path: 'content/topic-activities',
        name: 'topic-activity-list',
        component: () => import('@/views/content/TopicActivityListView.vue'),
        meta: {
          title: '专题活动',
          permission: permissions.contentView,
          menuKey: '/content/topic-activities',
        },
      },
      {
        path: 'content/topic-activities/new',
        name: 'topic-activity-create',
        component: () => import('@/views/content/TopicActivityFormView.vue'),
        meta: { title: '新增专题活动', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/topic-activities/:id/edit',
        name: 'topic-activity-edit',
        component: () => import('@/views/content/TopicActivityFormView.vue'),
        meta: { title: '编辑专题活动', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/topic-activities/:id',
        name: 'topic-activity-detail',
        component: () => import('@/views/content/TopicActivityFormView.vue'),
        meta: { title: '专题活动详情', permission: permissions.contentView, hidden: true },
      },
      {
        path: 'content/invitation-posters/new',
        name: 'invitation-poster-create',
        component: () => import('@/views/content/InvitationPosterFormView.vue'),
        meta: { title: '新增邀请海报', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/invitation-posters/:id/edit',
        name: 'invitation-poster-edit',
        component: () => import('@/views/content/InvitationPosterFormView.vue'),
        meta: { title: '编辑邀请海报', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/invitation-posters/:id',
        name: 'invitation-poster-detail',
        component: () => import('@/views/content/InvitationPosterFormView.vue'),
        meta: { title: '邀请海报详情', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/push-messages',
        name: 'push-message-list',
        component: () => import('@/views/content/PushMessageListView.vue'),
        meta: {
          title: 'App 推送',
          permission: permissions.advertisingView,
          menuKey: '/content/push-messages',
        },
      },
      {
        path: 'content/push-messages/new',
        name: 'push-message-create',
        component: () => import('@/views/content/PushMessageFormView.vue'),
        meta: { title: '新增 App 推送', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/push-messages/:id/edit',
        name: 'push-message-edit',
        component: () => import('@/views/content/PushMessageFormView.vue'),
        meta: { title: '编辑 App 推送', permission: permissions.advertisingView, hidden: true },
      },
      {
        path: 'content/push-messages/:id',
        name: 'push-message-detail',
        component: () => import('@/views/content/PushMessageFormView.vue'),
        meta: { title: 'App 推送详情', permission: permissions.advertisingView, hidden: true },
      },
      ...(['android', 'ios', 'harmony'] as const).flatMap((platform) => {
        const label = { android: 'Android', ios: 'iOS', harmony: '鸿蒙' }[platform]
        return [
          {
            path: `versions/${platform}`,
            name: `${platform}-version-list`,
            component: () => import('@/views/version/AppVersionListView.vue'),
            props: { platform },
            meta: {
              title: `${label} 版本`,
              permission: permissions.versionView,
              menuKey: `/versions/${platform}`,
            },
          },
          {
            path: `versions/${platform}/new`,
            name: `${platform}-version-create`,
            component: () => import('@/views/version/AppVersionFormView.vue'),
            props: { platform },
            meta: {
              title: `新增 ${label} 版本`,
              permission: permissions.versionView,
              hidden: true,
            },
          },
        ]
      }),
      {
        path: 'settings/sub-accounts',
        name: 'sub-account-list',
        component: () => import('@/views/settings/SubAccountListView.vue'),
        meta: {
          title: '子账号管理',
          permission: permissions.subAccountView,
          menuKey: '/settings/sub-accounts',
        },
      },
      {
        path: 'settings/sub-accounts/new',
        name: 'sub-account-create',
        component: () => import('@/views/settings/SubAccountFormView.vue'),
        meta: { title: '新增子账号', permission: permissions.subAccountView, hidden: true },
      },
      {
        path: 'settings/sub-accounts/:id/edit',
        name: 'sub-account-edit',
        component: () => import('@/views/settings/SubAccountFormView.vue'),
        meta: { title: '编辑子账号', permission: permissions.subAccountView, hidden: true },
      },
      {
        path: 'settings/sub-accounts/:id',
        name: 'sub-account-detail',
        component: () => import('@/views/settings/SubAccountFormView.vue'),
        meta: { title: '子账号详情', permission: permissions.subAccountView, hidden: true },
      },
      {
        path: 'settings/interfaces',
        name: 'interface-config',
        component: () => import('@/views/settings/InterfaceConfigView.vue'),
        meta: {
          title: '接口配置',
          permission: permissions.settingsView,
          menuKey: '/settings/interfaces',
        },
      },
      {
        path: 'settings/parameters',
        name: 'parameter-config',
        component: () => import('@/views/settings/ParameterConfigView.vue'),
        meta: {
          title: '参数配置',
          permission: permissions.parameterConfigView,
          menuKey: '/settings/parameters',
        },
      },
      {
        path: 'result/success',
        name: 'result-success',
        component: () => import('@/views/results/OperationResultView.vue'),
        props: { status: 'success' },
        meta: { title: '操作成功', permission: permissions.dashboardView, hidden: true },
      },
      {
        path: 'result/fail',
        name: 'result-fail',
        component: () => import('@/views/results/OperationResultView.vue'),
        props: { status: 'error' },
        meta: { title: '操作失败', permission: permissions.dashboardView, hidden: true },
      },
      {
        path: '500',
        name: 'server-error',
        component: () => import('@/views/errors/ServerErrorView.vue'),
        meta: { title: '服务器异常', permission: permissions.dashboardView, hidden: true },
      },
      {
        path: '403',
        name: 'forbidden',
        component: () => import('@/views/errors/ForbiddenView.vue'),
        meta: { title: '无权限', hidden: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: { title: '页面不存在', public: true },
  },
]
